import { certificationForItemLevel, PLAYER_LEVEL_MAX } from '../progression/guild';
import {
  CONSUMABLE_RARITY_EFFECT_COUNT,
  consumableStackKey,
  normalizeConsumableBatch,
  type ConsumableBatch,
  type ConsumableEffectRoll,
  type ConsumableRarity,
  type ConsumableTraitKind,
  type GenerateConsumableInput,
} from './procedural';
import {
  consumableFormulaSnapshot,
  type ConsumableFormulaSnapshot,
  type ConsumableSystemConfig,
} from './config';

export type ConfiguredConsumableBatch = ConsumableBatch & { formula: ConsumableFormulaSnapshot };

function safeRandom(rng: () => number): number {
  const value = Number(rng());
  return Number.isFinite(value) ? Math.min(0.999999999, Math.max(0, value)) : 0;
}

function drawStars(rng: () => number): 1 | 2 | 3 | 4 | 5 {
  const roll = safeRandom(rng);
  return roll < 0.10 ? 1 : roll < 0.30 ? 2 : roll < 0.70 ? 3 : roll < 0.92 ? 4 : 5;
}

function shuffledUnique<T>(values: readonly T[], count: number, rng: () => number): T[] {
  const copy = [...new Set(values)];
  for (let index = copy.length - 1; index > 0; index--) {
    const other = Math.floor(safeRandom(rng) * (index + 1));
    [copy[index], copy[other]] = [copy[other]!, copy[index]!];
  }
  return copy.slice(0, count);
}

function restoreEffect(resource: any, itemLevel: number, formula: ConsumableFormulaSnapshot): ConsumableEffectRoll {
  return {
    kind: 'restore',
    resource,
    flat: Math.max(1, Math.round((formula.restore.base + itemLevel * formula.restore.perLevel)
      * formula.starMultiplier * formula.rarityCoreMultiplier)),
    pctMax: formula.restore.pctMax,
  };
}

function traitEffect(kind: ConsumableTraitKind, itemLevel: number, formula: ConsumableFormulaSnapshot, rng: () => number): ConsumableEffectRoll {
  if (kind === 'barrier') return {
    kind, value: Math.round((formula.barrier.base + itemLevel * formula.barrier.perLevel) * formula.starMultiplier),
    duration: formula.barrier.duration,
  };
  if (kind === 'regen') return { kind, pctMax: formula.regen.pctMax, duration: formula.regen.duration };
  if (kind === 'cleanse') return { kind, count: formula.cleanseCount };
  return {
    kind: 'status',
    statusId: formula.status.ids[Math.floor(safeRandom(rng) * formula.status.ids.length)]!,
    duration: formula.status.duration,
  };
}

function canonicalEffect(effect: any): ConsumableEffectRoll {
  const normalized: ConsumableEffectRoll = { kind: effect?.kind };
  for (const key of ['resource', 'flat', 'pctMax', 'value', 'statusId', 'duration', 'count'] as const) {
    if (effect?.[key] !== undefined) (normalized as any)[key] = key === 'resource' || key === 'statusId' ? String(effect[key]) : Number(effect[key]);
  }
  return normalized;
}

export function normalizeConsumableFormula(raw: any): ConsumableFormulaSnapshot | null {
  if (!raw || raw.schemaVersion !== 1) return null;
  const finite = (value: any, min: number, max: number) => Number.isFinite(Number(value)) && Number(value) >= min && Number(value) <= max;
  if (!finite(raw.restore?.base, 0, 5000) || !finite(raw.restore?.perLevel, 0, 100) || !finite(raw.restore?.pctMax, 0, 0.5)
    || !finite(raw.rarityCoreMultiplier, 0.1, 10) || !finite(raw.starMultiplier, 0.1, 10)
    || !finite(raw.barrier?.base, 0, 5000) || !finite(raw.barrier?.perLevel, 0, 100)
    || !Number.isInteger(raw.barrier?.duration) || !finite(raw.barrier?.duration, 1, 20)
    || !finite(raw.regen?.pctMax, 0.001, 0.5) || !Number.isInteger(raw.regen?.duration) || !finite(raw.regen?.duration, 1, 20)
    || !Number.isInteger(raw.cleanseCount) || !finite(raw.cleanseCount, 1, 20)
    || !Number.isInteger(raw.status?.duration) || !finite(raw.status?.duration, 1, 20)
    || !Array.isArray(raw.status?.ids) || raw.status.ids.length < 1 || raw.status.ids.length > 20
    || raw.status.ids.some((id: any) => !/^[a-zA-Z0-9_-]{1,40}$/.test(String(id)))) return null;
  return {
    schemaVersion: 1,
    restore: { base: Number(raw.restore.base), perLevel: Number(raw.restore.perLevel), pctMax: Number(raw.restore.pctMax) },
    rarityCoreMultiplier: Number(raw.rarityCoreMultiplier),
    starMultiplier: Number(raw.starMultiplier),
    barrier: { base: Number(raw.barrier.base), perLevel: Number(raw.barrier.perLevel), duration: Number(raw.barrier.duration) },
    regen: { pctMax: Number(raw.regen.pctMax), duration: Number(raw.regen.duration) },
    cleanseCount: Number(raw.cleanseCount),
    status: { ids: [...new Set<string>(raw.status.ids.map((id: unknown) => String(id)))], duration: Number(raw.status.duration) },
  };
}

export function generateConfiguredConsumableBatch(
  input: GenerateConsumableInput & { config: ConsumableSystemConfig; preferredTraits?: ConsumableTraitKind[] },
): ConfiguredConsumableBatch {
  const rng = input.rng || Math.random;
  const itemLevel = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Math.floor(Number(input.itemLevel) || 1)));
  const qualityStars = drawStars(rng);
  const formula = consumableFormulaSnapshot(input.config, input.template.resource, input.rarity, qualityStars);
  const templateConfig = input.config.templates[input.template.id];
  if (!templateConfig?.enabled) throw new Error(`Modèle ${input.template.id} désactivé.`);
  const traitCount = Math.max(0, CONSUMABLE_RARITY_EFFECT_COUNT[input.rarity] - 1);
  /* `preferredTraits` sert aux lots que la boutique DOIT garantir : sans lui,
   * le tirage pouvait ne proposer aucune potion à effet prolongé de toute une
   * rotation. Les traits demandés passent devant, le reste comble au hasard —
   * un trait non autorisé par le modèle est simplement ignoré, jamais forcé. */
  const preferred = (input.preferredTraits || []).filter((trait) => templateConfig.allowedTraits.includes(trait));
  const remaining = Math.max(0, traitCount - preferred.length);
  const filler = shuffledUnique(
    templateConfig.allowedTraits.filter((trait) => !preferred.includes(trait)),
    Math.min(remaining, templateConfig.allowedTraits.length),
    rng,
  );
  const traits = [...preferred.slice(0, traitCount), ...filler];
  const effects = [
    restoreEffect(input.template.resource, itemLevel, formula),
    ...traits.map((trait) => traitEffect(trait, itemLevel, formula, rng)),
  ];
  // Une rareté doit toujours garder son nombre de propriétés. Les modèles mal
  // configurés sont refusés par l'admin, mais ce garde-fou évite un lot ambigu.
  if (effects.length !== CONSUMABLE_RARITY_EFFECT_COUNT[input.rarity]) {
    throw new Error(`Pool de propriétés insuffisant pour la rareté ${input.rarity}.`);
  }
  const identity = {
    templateId: input.template.id,
    itemLevel,
    rarity: input.rarity,
    qualityStars,
    effects,
    requiredCertification: certificationForItemLevel(itemLevel),
  };
  return {
    ...identity,
    stackKey: consumableStackKey(identity),
    formula,
    source: { ...input.source },
    generatedAt: input.generatedAt ?? Date.now(),
  };
}

export function normalizeConfiguredConsumableBatch(raw: any): ConfiguredConsumableBatch | null {
  if (!raw?.formula) return null;
  const formula = normalizeConsumableFormula(raw.formula);
  const itemLevel = Number(raw.itemLevel);
  const qualityStars = Number(raw.qualityStars) as 1 | 2 | 3 | 4 | 5;
  const rarity = raw.rarity as ConsumableRarity;
  const effects = Array.isArray(raw.effects) ? raw.effects.map(canonicalEffect) : [];
  const restore = effects[0];
  if (!formula || !Number.isInteger(itemLevel) || itemLevel < 1 || itemLevel > PLAYER_LEVEL_MAX
    || ![1, 2, 3, 4, 5].includes(qualityStars)
    || !(rarity in CONSUMABLE_RARITY_EFFECT_COUNT)
    || effects.length !== CONSUMABLE_RARITY_EFFECT_COUNT[rarity]
    || raw.requiredCertification !== certificationForItemLevel(itemLevel)
    || restore?.kind !== 'restore' || !['hp', 'mp', 'stamina'].includes(String(restore.resource))) return null;
  const expectedRestore = restoreEffect(restore.resource, itemLevel, formula);
  if (JSON.stringify(restore) !== JSON.stringify(expectedRestore)) return null;
  const seen = new Set<string>();
  for (const effect of effects.slice(1)) {
    if (!['barrier', 'regen', 'cleanse', 'status'].includes(effect.kind) || seen.has(effect.kind)) return null;
    seen.add(effect.kind);
    let expected: ConsumableEffectRoll;
    if (effect.kind === 'status') {
      if (!formula.status.ids.includes(String(effect.statusId))) return null;
      expected = { kind: 'status', statusId: effect.statusId, duration: formula.status.duration };
    } else expected = traitEffect(effect.kind as ConsumableTraitKind, itemLevel, formula, () => 0);
    if (JSON.stringify(effect) !== JSON.stringify(expected)) return null;
  }
  const source = raw.source && typeof raw.source === 'object' ? { ...raw.source } : { kind: 'legacy' };
  const identity = {
    templateId: String(raw.templateId), itemLevel, rarity, qualityStars, effects,
    requiredCertification: raw.requiredCertification,
  };
  return {
    ...identity,
    stackKey: consumableStackKey(identity),
    formula,
    source,
    generatedAt: Number.isFinite(Number(raw.generatedAt)) ? Number(raw.generatedAt) : 0,
  } as ConfiguredConsumableBatch;
}

export function normalizeAnyConsumableBatch(raw: any): ConsumableBatch | null {
  return raw?.formula ? normalizeConfiguredConsumableBatch(raw) : normalizeConsumableBatch(raw);
}
